import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import { get } from "lib/api";
import { firstMediaUrl, mediaUrl } from "lib/media";
import { useCart } from "context/CartContext";
import { useAuth } from "context/AuthContext";
import { currencyForCompany } from "lib/companyProfile";
import { getTextLocale } from "lib/storefront";
import {
  normalizeSpecEntries,
  normalizeTechnicalSections,
  normalizeTransmissionNames,
  productStoryText,
} from "storefronts/shared/pimProductContent";
import StorefrontLayout from "../components/StorefrontLayout";
import EdsIcon from "../components/EdsIcon";
import { EDS, priceText, resolvePrice, btnPrimary, btnGhost, resetButton } from "../theme";

function normalizeProduct(data) {
  if (!data?.data) return null;
  return data.data.attributes
    ? {
        id: data.data.id,
        documentId: data.data.documentId || data.data.id,
        ...data.data.attributes,
      }
    : { ...data.data, documentId: data.data.documentId || data.data.id };
}

function HtmlOrText({ value }) {
  if (!value) return null;
  if (String(value).includes("<")) {
    return (
      <Box
        sx={{
          color: EDS.muted,
          lineHeight: 1.75,
          fontSize: "0.9375rem",
          fontWeight: 400,
          "& p": { m: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }
  return (
    <Box
      component="p"
      sx={{
        m: 0,
        color: EDS.muted,
        lineHeight: 1.75,
        fontSize: "0.9375rem",
        fontWeight: 400,
        whiteSpace: "pre-wrap",
      }}
    >
      {value}
    </Box>
  );
}

HtmlOrText.propTypes = {
  value: PropTypes.string,
};

HtmlOrText.defaultProps = {
  value: "",
};

export default function ProductDetailPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem, lines } = useCart();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const canOrder = !!token;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [orderQuantity, setOrderQuantity] = useState(1);
  const [cartNotice, setCartNotice] = useState("");

  const isMobileRoute = location.pathname.startsWith("/m/catalog/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  const companyKey = String(storefrontTenant || activeCompany || "eds").toLowerCase();
  const textLocale = getTextLocale();

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "eds-product",
      documentId,
      companyKey,
      textLocale,
      token ? "auth" : "public",
    ],
    queryFn: () => get(`/api/storefront/catalog/${documentId}`),
    enabled: !!documentId,
    staleTime: 30000,
  });

  const currency =
    data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);
  const product = normalizeProduct(data);
  const effectivePrice = resolvePrice(product);
  const hasEffectivePrice = Number(effectivePrice) > 0;
  const coreCharge = Number(product?.coreCharge || 0);
  const images = Array.isArray(product?.images?.data)
    ? product.images.data
    : Array.isArray(product?.images)
      ? product.images
      : [];
  const selectedImage = mediaUrl(images[selectedImageIndex]) || null;
  const unitPriceNumber = Number(effectivePrice || 0);
  const lineTotal = hasEffectivePrice
    ? (unitPriceNumber + coreCharge) * orderQuantity
    : 0;
  const inCartQuantity =
    (lines || []).find(
      (line) => String(line.documentId) === String(product?.documentId),
    )?.qty || 0;
  const story = productStoryText(product);
  const unitDetails = String(product?.unitSpecificDetails || "").trim();
  const installationNotes = String(product?.installationNotes || "").trim();
  const fitmentYears = String(product?.fitmentYears || "").trim();
  const transmissions = normalizeTransmissionNames(product?.transmissions);
  const specEntries = normalizeSpecEntries(product?.specifications);
  const technicalSections = normalizeTechnicalSections(product?.technicalSections);

  const setQuantity = (value) => {
    const next = Math.max(1, Math.min(99, Math.floor(Number(value || 1) || 1)));
    setOrderQuantity(next);
  };

  const addProductToCart = (goToCart = false) => {
    if (!canOrder) {
      navigate(
        toPath(
          `/authentication/sign-in?next=${encodeURIComponent(location.pathname)}`,
        ),
      );
      return;
    }
    if (!product || !hasEffectivePrice) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: unitPriceNumber,
        coreCharge: product.coreCharge != null ? coreCharge : null,
        imageUrl: firstMediaUrl(product?.images),
      },
      orderQuantity,
    );
    setCartNotice(`${orderQuantity} added to cart`);
    if (goToCart) navigate(toPath("/cart"));
  };

  if (isLoading) {
    return (
      <StorefrontLayout>
        <Box sx={{ py: 10, color: EDS.muted, fontWeight: 400 }}>Loading product…</Box>
      </StorefrontLayout>
    );
  }

  if (error || !product) {
    return (
      <StorefrontLayout>
        <Box sx={{ py: 10, textAlign: "center" }}>
          <Box
            component="h1"
            sx={{ m: 0, fontWeight: 500, color: EDS.ink, letterSpacing: "-0.02em" }}
          >
            Product not found
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => navigate(toPath("/catalog"))}
            sx={{ ...btnPrimary, mt: 2.5 }}
          >
            Back to catalog
          </Box>
        </Box>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout maxWidth={null}>
      <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 6, md: 8 } }}>
        <Box
          component="button"
          type="button"
          onClick={() => navigate(toPath("/catalog"))}
          sx={{
            ...resetButton,
            color: EDS.muted,
            fontWeight: 400,
            fontSize: "0.875rem",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mb: 3,
            "&:hover": { color: EDS.ink },
          }}
        >
          <EdsIcon name="back" size={16} color="currentColor" />
          Catalog
        </Box>

        <Grid container spacing={{ xs: 4, md: 8 }}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: `${EDS.radiusLg}px`,
                overflow: "hidden",
                bgcolor: EDS.imageBg,
                aspectRatio: "1 / 1",
                display: "grid",
                placeItems: "center",
              }}
            >
              {selectedImage ? (
                <Box
                  component="img"
                  src={selectedImage}
                  alt={product.name}
                  sx={{ width: "100%", height: "100%", objectFit: "contain", p: 3 }}
                />
              ) : null}
            </Box>
            {images.length > 1 ? (
              <Box sx={{ mt: 2, display: "flex", gap: 1, overflowX: "auto" }}>
                {images.map((img, index) => {
                  const url = mediaUrl(img);
                  const active = index === selectedImageIndex;
                  return (
                    <Box
                      key={index}
                      component="button"
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      sx={{
                        ...resetButton,
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        borderRadius: "8px",
                        overflow: "hidden",
                        bgcolor: EDS.imageBg,
                        opacity: active ? 1 : 0.45,
                        outline: active ? `1px solid ${EDS.ink}` : "none",
                        outlineOffset: 1,
                        transition: "opacity 160ms ease",
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="span"
              sx={{ fontSize: "0.8125rem", fontWeight: 400, color: EDS.subtle }}
            >
              {product.sku || "SKU pending"}
            </Box>
            <Box
              component="h1"
              sx={{
                m: "0.4rem 0 0",
                fontSize: { xs: "1.5rem", md: "1.875rem" },
                fontWeight: 500,
                letterSpacing: "-0.03em",
                color: EDS.ink,
                lineHeight: 1.2,
              }}
            >
              {product.name}
            </Box>

            <Box sx={{ mt: 2, pb: 3, borderBottom: `1px solid ${EDS.line}` }}>
              <Box
                component="span"
                sx={{
                  fontSize: { xs: "1.375rem", md: "1.5rem" },
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  color: EDS.ink,
                }}
              >
                {priceText(effectivePrice, currency)}
              </Box>
              {coreCharge > 0 ? (
                <Box
                  component="p"
                  sx={{ m: "0.25rem 0 0", fontSize: "0.8125rem", color: EDS.muted, fontWeight: 400 }}
                >
                  + {priceText(coreCharge, currency)} refundable core deposit
                </Box>
              ) : null}
            </Box>

            {(transmissions.length || fitmentYears) ? (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {fitmentYears ? (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.4,
                      borderRadius: `${EDS.radius}px`,
                      bgcolor: EDS.panel,
                      border: `1px solid ${EDS.line}`,
                      fontSize: "0.75rem",
                      color: EDS.ink,
                      fontWeight: 500,
                    }}
                  >
                    Fitment: {fitmentYears}
                  </Box>
                ) : null}
                {transmissions.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      px: 1,
                      py: 0.4,
                      borderRadius: `${EDS.radius}px`,
                      border: `1px solid ${EDS.line}`,
                      fontSize: "0.75rem",
                      color: EDS.muted,
                      fontWeight: 400,
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            ) : null}

            {story ? (
              <Box sx={{ mt: 3, pb: 3, borderBottom: `1px solid ${EDS.line}` }}>
                <HtmlOrText value={story} />
              </Box>
            ) : null}

            {unitDetails ? (
              <Box sx={{ mt: 3, pb: 3, borderBottom: `1px solid ${EDS.line}` }}>
                <Box
                  component="h2"
                  sx={{ m: "0 0 0.75rem", fontSize: "0.9375rem", fontWeight: 500, color: EDS.ink }}
                >
                  Unit details
                </Box>
                <HtmlOrText value={unitDetails} />
              </Box>
            ) : null}

            {hasEffectivePrice ? (
              <Box sx={{ mt: 3 }}>
                {inCartQuantity > 0 || cartNotice ? (
                  <Box
                    component="p"
                    sx={{
                      m: "0 0 1.25rem",
                      fontSize: "0.8125rem",
                      color: EDS.link,
                      fontWeight: 400,
                    }}
                  >
                    {cartNotice || `${inCartQuantity} already in your cart`}
                  </Box>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setQuantity(orderQuantity - 1)}
                      aria-label="Decrease quantity"
                      sx={{
                        ...resetButton,
                        color: EDS.ink,
                        display: "grid",
                        placeItems: "center",
                        p: 0.25,
                        opacity: 0.7,
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      <EdsIcon name="remove" size={18} color={EDS.ink} />
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        minWidth: 24,
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "0.9375rem",
                      }}
                    >
                      {orderQuantity}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setQuantity(orderQuantity + 1)}
                      aria-label="Increase quantity"
                      sx={{
                        ...resetButton,
                        color: EDS.ink,
                        display: "grid",
                        placeItems: "center",
                        p: 0.25,
                        opacity: 0.7,
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      <EdsIcon name="add" size={18} color={EDS.ink} />
                    </Box>
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: EDS.muted, fontSize: "0.875rem", fontWeight: 400 }}
                  >
                    {priceText(lineTotal, currency)} total
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => addProductToCart(false)}
                    sx={{ ...btnPrimary, minWidth: 148 }}
                  >
                    {canOrder ? "Add to cart" : "Sign in to order"}
                  </Box>
                  {canOrder ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => addProductToCart(true)}
                      sx={btnGhost}
                    >
                      Buy now
                    </Box>
                  ) : null}
                </Box>
              </Box>
            ) : (
              <Box
                component="p"
                sx={{ mt: 3, color: EDS.muted, fontSize: "0.9375rem", fontWeight: 400 }}
              >
                Contact us for pricing on this item.
              </Box>
            )}

            {specEntries.length ? (
              <Box sx={{ mt: 5 }}>
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mb: 1.5,
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    color: EDS.ink,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Specifications
                </Box>
                {specEntries.map(([key, val], i) => (
                  <Box
                    key={key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.2fr",
                      gap: 2,
                      py: 1.1,
                      borderTop: i === 0 ? `1px solid ${EDS.line}` : "none",
                      borderBottom: `1px solid ${EDS.line}`,
                      fontSize: "0.875rem",
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 400, color: EDS.muted }}>
                      {key}
                    </Box>
                    <Box component="span" sx={{ color: EDS.ink, fontWeight: 400 }}>
                      {val}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}

            {installationNotes ? (
              <Box sx={{ mt: 5 }}>
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mb: 1.5,
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    color: EDS.ink,
                  }}
                >
                  Installation notes
                </Box>
                <HtmlOrText value={installationNotes} />
              </Box>
            ) : null}

            {technicalSections.length ? (
              <Box sx={{ mt: 5 }}>
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mb: 1.5,
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    color: EDS.ink,
                  }}
                >
                  Product details
                </Box>
                <Box sx={{ display: "grid", gap: 2 }}>
                  {technicalSections.map((section, index) => (
                    <Box
                      key={`${section.title || "section"}-${index}`}
                      sx={{ bgcolor: EDS.panel, borderRadius: `${EDS.radius}px`, p: 2 }}
                    >
                      {section.title ? (
                        <Box
                          component="h3"
                          sx={{ m: "0 0 0.75rem", fontSize: "0.9375rem", fontWeight: 500, color: EDS.ink }}
                        >
                          {section.title}
                        </Box>
                      ) : null}
                      {section.body ? <HtmlOrText value={section.body} /> : null}
                      {section.images.length ? (
                        <Box
                          sx={{
                            mt: 1.5,
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                            gap: 1,
                          }}
                        >
                          {section.images.map((img, imageIndex) => {
                            const url = mediaUrl(img);
                            if (!url) return null;
                            return (
                              <Box
                                key={`${index}-${imageIndex}`}
                                component="img"
                                src={url}
                                alt={section.title || "Product details"}
                                sx={{
                                  width: "100%",
                                  aspectRatio: "1 / 1",
                                  objectFit: "cover",
                                  borderRadius: `${EDS.radius}px`,
                                  bgcolor: EDS.imageBg || EDS.panel,
                                }}
                              />
                            );
                          })}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Container>
    </StorefrontLayout>
  );
}
